// EntityFilterBuilder: Combines entity selection with FilterBuilder

import React, { useState, useEffect, useCallback } from "react";
import { EntityCombobox } from "../entity-mappings/entity-combobox";
import { FilterBuilder } from "./FilterBuilder";
import { fetchEntityFields, countDataverseRecords, startFilteredDataRetrieval } from "../../lib/api";
import { DataverseEntityInfo, DataverseFieldInfo } from "@shared/api-types";
import { FilterObject, FieldType } from "../../../shared/filter-types";
import { Card } from "../ui/card";
import { Button } from "../ui/button";

type EntityFilterBuilderProps = {
  entities: DataverseEntityInfo[];
  onChange: (state: { entity: string | null; filter: FilterObject | null }) => void;
  initialEntity?: string;
  initialFilter?: FilterObject;
  isLoadingEntities?: boolean;
};

export const EntityFilterBuilder: React.FC<EntityFilterBuilderProps> = ({
  entities,
  onChange,
  initialEntity,
  initialFilter,
  isLoadingEntities = false,
}) => {
  const [selectedEntity, setSelectedEntity] = useState<string | null>(initialEntity ?? null);
  const [fields, setFields] = useState<DataverseFieldInfo[]>([]);
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [filter, setFilter] = useState<FilterObject | null>(initialFilter ?? null);

// Debug logs for diagnosis
useEffect(() => {
  console.log("[EntityFilterBuilder] selectedEntity:", selectedEntity);
}, [selectedEntity]);
useEffect(() => {
  console.log("[EntityFilterBuilder] filter:", filter);
  console.log("[EntityFilterBuilder] isValidFilter:", !!(filter && Array.isArray(filter.and) && filter.and.length > 0));
}, [filter]);
  // Data Retrieval state
  const [retrievalLoading, setRetrievalLoading] = useState(false);
  const [retrievalError, setRetrievalError] = useState<string | null>(null);
  const [retrievalJob, setRetrievalJob] = useState<any>(null);

  // Count Records state
  const [count, setCount] = useState<number | null>(null);
  const [countLoading, setCountLoading] = useState(false);
  const [countError, setCountError] = useState<string | null>(null);

  // 1. Add a state variable to track if the current filter has been counted.
  const [hasCounted, setHasCounted] = useState(false);

  // Fetch fields when entity changes
  useEffect(() => {
    if (!selectedEntity) {
      setFields([]);
      setFilter(null);
      onChange({ entity: null, filter: null });
      setCount(null);
      setCountError(null);
      return;
    }
    setFieldsLoading(true);
    fetchEntityFields(selectedEntity)
      .then((fetchedFields) => {
        setFields(fetchedFields ?? []);
        setFilter(null); // Reset filter when entity changes
        onChange({ entity: selectedEntity, filter: null });
        setCount(null);
        setCountError(null);
      })
      .finally(() => setFieldsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntity]);

  // Handle filter changes
  // 2. In handleFilterChange, reset hasCounted to false on any filter change.
  const handleFilterChange = useCallback(
    (newFilter: FilterObject) => {
      setFilter(newFilter);
      onChange({ entity: selectedEntity, filter: newFilter });
      setCount(null);
      setCountError(null);
      setHasCounted(false); // Require recount after filter change
    },
    [onChange, selectedEntity]
  );

  // Map DataverseFieldInfo to FilterBuilder's FieldDef
  const fieldDefs =
    fields?.map((f) => {
      // Defensive: ensure type is valid, default to "string"
      let rawType = f.type ?? f.AttributeType ?? "string";
      const validTypes: FieldType[] = ["string", "number", "boolean", "date"];
      const type: FieldType = validTypes.includes(rawType as FieldType)
        ? (rawType as FieldType)
        : "string";
      return {
        name: f.logicalName ?? f.LogicalName ?? "",
        type,
      };
    }) ?? [];

  // Validate filter: must have at least one condition
  const isValidFilter = !!(filter && Array.isArray(filter.and) && filter.and.length > 0);

  // Count Records handler
  // 3. In handleCountRecords, set hasCounted to true after a successful count.
  const handleCountRecords = async () => {
    if (!selectedEntity || !isValidFilter) return;
    setCountLoading(true);
    setCountError(null);
    setCount(null);
    try {
      const result = await countDataverseRecords(selectedEntity, filter!);
      setCount(result);
      setHasCounted(true); // Mark as counted
    } catch (err: any) {
      setCountError(err?.message || "Failed to count records");
      setHasCounted(false);
    } finally {
      setCountLoading(false);
    }
  };

  return (
    <Card className="p-4 space-y-4">
      <div>
        <EntityCombobox
          entities={entities}
          value={selectedEntity ?? ""}
          onChange={setSelectedEntity}
          isLoading={isLoadingEntities}
          placeholder="Select an entity"
        />
      </div>
      {fieldsLoading && (
        <div className="flex items-center gap-2 text-muted-foreground">
          <span className="animate-spin mr-2">⏳</span> Loading fields...
        </div>
      )}
      {!fieldsLoading && selectedEntity && (
        <>
          <FilterBuilder
            fields={fieldDefs}
            onChange={handleFilterChange}
            initial={undefined}
          />
          <div className="flex items-center gap-4 mt-2">
            <Button
              onClick={handleCountRecords}
              disabled={!isValidFilter || countLoading}
              variant="secondary"
            >
              {countLoading ? (
                <>
                  <span className="animate-spin mr-2">⏳</span> Counting...
                </>
              ) : (
                "Count Records"
              )}
            </Button>
            {/* 4. Disable "Start Data Retrieval" unless hasCounted is true and filter is valid. */}
            <Button
              onClick={async () => {
                setRetrievalLoading(true);
                setRetrievalError(null);
                setRetrievalJob(null);
                try {
                  const job = await startFilteredDataRetrieval(selectedEntity!, filter!);
                  setRetrievalJob(job);
                } catch (err: any) {
                  setRetrievalError(err?.message || "Failed to start data retrieval");
                } finally {
                  setRetrievalLoading(false);
                }
              }}
              disabled={!isValidFilter || !hasCounted || retrievalLoading}
              variant="default"
            >
              {retrievalLoading ? (
                <>
                  <span className="animate-spin mr-2">⏳</span> Starting...
                </>
              ) : (
                "Start Data Retrieval"
              )}
            </Button>
            {count !== null && (
              <span className="text-green-700 font-medium">
                {count} record{count === 1 ? "" : "s"} found
              </span>
            )}
            {countError && (
              <span className="text-red-600">{countError}</span>
            )}
          </div>
          {retrievalError && (
            <div className="text-red-600 mt-2">{retrievalError}</div>
          )}
          {retrievalJob && (
            <div className="text-green-700 mt-2">
              Data retrieval started. Batch ID: <span className="font-mono">{retrievalJob.batchId || retrievalJob.id || JSON.stringify(retrievalJob)}</span>
            </div>
          )}
        </>
      )}
    </Card>
  );
};